/**
 * groupAttemptsByPuzzle
 *
 * Reconstructs the per-puzzle attempt trail for a single player from the
 * flat `responses[playerId]` array carried in `fun_session_end`. The
 * PositionTrainerAdapter emits each terminal move as the overloaded wire
 * string `"<puzzleId>:<uci>:<0|1>"` (where the trailing bit is the server-
 * authoritative correctness — see skill.md pitfall #17 / wire-payload
 * overload pattern).
 *
 * Returned shape matches what the result POST endpoint expects.
 */

export interface PuzzleAttempt {
  puzzleId: number;
  attempts: string[];
  solved: boolean;
}

export interface GroupedAttempts {
  attempts: PuzzleAttempt[];
  solvedIds: number[];
  failedIds: number[];
}

export function groupAttemptsByPuzzle(responses: string[]): GroupedAttempts {
  const order: number[] = [];           // first-seen puzzle order
  const byId: Map<number, PuzzleAttempt> = new Map();

  for (const raw of responses || []) {
    if (typeof raw !== 'string' || !raw) continue;
    // Expected format: "<puzzleId>:<uci>:<0|1>". Defensive: any line that
    // doesn't have at least two colons is ignored — the server treats the
    // square field as opaque, so a malformed adapter shouldn't crash the
    // reconstructor.
    const firstColon = raw.indexOf(':');
    const lastColon = raw.lastIndexOf(':');
    if (firstColon === -1 || lastColon === firstColon) continue;

    const puzzleIdStr = raw.slice(0, firstColon);
    const uci = raw.slice(firstColon + 1, lastColon);
    const correctnessStr = raw.slice(lastColon + 1);

    const puzzleId = parseInt(puzzleIdStr, 10);
    if (!Number.isFinite(puzzleId)) continue;

    const solved = correctnessStr === '1' || correctnessStr === 'true';

    let entry = byId.get(puzzleId);
    if (!entry) {
      entry = {puzzleId, attempts: [], solved: false};
      byId.set(puzzleId, entry);
      order.push(puzzleId);
    }
    if (uci) entry.attempts.push(uci);
    // The terminal correctness bit wins — the adapter only emits onMove on
    // solve-or-wrong-end-of-puzzle, so the last seen value is the true
    // outcome.
    entry.solved = solved;
  }

  const attempts = order.map(id => byId.get(id)!);
  const solvedIds: number[] = [];
  const failedIds: number[] = [];
  for (const a of attempts) {
    (a.solved ? solvedIds : failedIds).push(a.puzzleId);
  }
  return {attempts, solvedIds, failedIds};
}
